import { memo, useCallback } from 'react';
import classnames from 'classnames';

import styles from './ShareButton.module.scss';

import BaseButton from '@/components/BaseButton/BaseButton';

import Clipboard from '@/components/svgs/Clipboard.svg';

export type Props = {
  className?: string;
  title?: string;
  description?: string;
  src: string;
  fileName: string;
  canShare: boolean;
};

function ShareButton({ className, title, description, src, fileName, canShare }: Props) {
  const getShareableFiles = useCallback(async (): Promise<[File]> => {
    const blob = await fetch(src).then((resp) => resp.blob());
    return [new File([blob], fileName, { type: blob.type })];
  }, [src, fileName]);

  const handleSharing = useCallback(async () => {
    const files = await getShareableFiles();
    const shareData = {
      title: title,
      text: description,
      files
    };
    if (canShare && navigator?.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error(`Couldn't share because: ${error}`);
      }
    } else {
      console.warn('Web share is currently not supported on this browser');
    }
  }, [description, canShare, title, getShareableFiles]);

  return (
    <BaseButton
      title={title}
      aria-label={description}
      className={classnames(styles.ShareButton, className)}
      onClick={handleSharing}
    >
      <Clipboard className={styles.clipboard} />
    </BaseButton>
  );
}

export default memo(ShareButton);
